import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { BillingApiService } from './billing-api.service';
import { BillingStatus } from './billing.models';

/**
 * The firm's billing status, shared by the billing gate (pop-ups) and the billing banner. Loaded once when the app shell
 * starts and again after anything that changes it (Keep my plan, a confirmed payment, …).
 */
@Injectable({ providedIn: 'root' })
export class BillingStateService {
    private readonly status = new BehaviorSubject<BillingStatus | null>(null);
    private readonly changePlanRequests = new Subject<void>();
    private readonly planChanges = new Subject<void>();

    /** Null until the first load finishes. */
    readonly status$: Observable<BillingStatus | null> = this.status.asObservable();

    /** Settings → Billing → Change plan asks the billing gate to open the plan cards. */
    readonly changePlanRequests$: Observable<void> = this.changePlanRequests.asObservable();

    /** The plan was changed (or a pending switch dropped): Settings → Billing reloads its details. */
    readonly planChanges$: Observable<void> = this.planChanges.asObservable();

    /** The Teams tab (an iframe): Stripe Checkout and the billing portal open in the browser instead. */
    readonly isTeams = window !== window.parent && !window.opener;

    constructor(private api: BillingApiService) {}

    get current(): BillingStatus | null {
        return this.status.value;
    }

    refresh(): void {
        this.api
            .getStatus()
            .pipe(take(1))
            .subscribe({
                next: status => this.status.next(status),
                error: () => this.status.next(this.status.value),
            });
    }

    /** Local change after a successful action, without waiting for the next load. */
    patch(changes: Partial<BillingStatus>): void {
        if (this.status.value) {
            this.status.next({ ...this.status.value, ...changes });
        }
    }

    openChangePlan(): void {
        this.changePlanRequests.next();
    }

    notifyPlanChanged(): void {
        this.planChanges.next();
        this.refresh();
    }

    /** Stripe's hosted pages cannot run inside the Teams iframe. */
    openStripePage(url: string): void {
        if (this.isTeams) {
            window.open(url, '_blank');
        } else {
            window.location.href = url;
        }
    }
}
